import { computed, ref } from 'vue'
import { parseAccount } from '@drama/contracts'
import { countOf, emptyMergeCounts, MERGE_COUNT_FIELDS, mergeTotal } from '../lib/index.js'

const ACCOUNT_CACHE_KEY = 'visualGame.account'
// 结账回跳提示挂多久。到点自动收起，不再靠退出登录才清。
const CHECKOUT_NOTICE_MS = 3000
// checkoutStatus 每个取值对应一句文案，三个入口（结账页、移动端、游戏内浮层）共用这一张表。
const CHECKOUT_NOTICE_KEYS = {
  pending: 'visualGame.billing.checkoutPending',
  credited: 'visualGame.billing.checkoutCredited',
  confirming: 'visualGame.billing.checkoutConfirming',
  cancel: 'visualGame.billing.checkoutCanceled',
}

const AUTH_ERROR_MSG = {
  zh: {
    INVALID_CREDENTIALS: '邮箱或密码不正确',
    TOO_MANY_ATTEMPTS: '尝试次数过多，请稍后再试',
    USER_NOT_FOUND: '账号不存在',
    CODE_EXPIRED: '验证码已过期，请重新获取',
    CODE_INVALID: '验证码错误，请重新输入',
    _default: '登录失败，请检查信息后重试',
  },
  en: {
    INVALID_CREDENTIALS: 'Email or password is incorrect.',
    TOO_MANY_ATTEMPTS: 'Too many attempts. Please try again later.',
    USER_NOT_FOUND: 'Account not found.',
    CODE_EXPIRED: 'Code has expired. Please request a new one.',
    CODE_INVALID: 'Invalid code. Please check and try again.',
    _default: 'Sign in failed. Please check your details and try again.',
  },
}

// authenticate 的三种入口各自对应的 api.auth 方法。
const AUTH_REQUESTS = { login: 'login', register: 'register', oauth: 'oauthLogin', emailCode: 'loginWithEmailCode' }

function friendlyAuthError(err, locale) {
  const code = err?.code
  const lang = locale?.startsWith('zh') ? 'zh' : 'en'
  const msgs = AUTH_ERROR_MSG[lang] || AUTH_ERROR_MSG.en
  return msgs[code] ?? msgs._default
}

export function createVisualAccount(api, { analytics, getLocale = () => 'zh' } = {}) {
  if (!analytics || typeof analytics.track !== 'function') throw new Error('Visual account analytics provider is required')
  // 首屏先用缓存里的账号顶一下。判据是「有没有会话」而不是「access token 还新鲜吗」，
  // 否则 token 一过期，首屏会闪一下未登录，随后 load() 又把人登回来。
  const account = ref(api.tokenStore.getAccessToken() || api.tokenStore.getRefreshToken() ? readCachedAccount() : null)
  const authBusy = ref(false)
  const billingBusy = ref(false)
  const checkoutBusy = ref(false)
  const error = ref('')
  const billingError = ref('')
  const checkoutUrl = ref('')
  // '' | 'pending'（正在跳去结账）| 'credited'（回来时订单已 paid）| 'confirming'（回来时 webhook 还没到）| 'cancel'
  const checkoutStatus = ref('')
  const checkoutPoints = ref(0)
  let checkoutNoticeTimer = null
  const checkoutNotice = computed(() => (checkoutStatus.value === ''
    ? null
    : { kind: checkoutStatus.value, key: CHECKOUT_NOTICE_KEYS[checkoutStatus.value], params: { points: checkoutPoints.value } }))
  const mergeResult = ref(null)
  const products = ref([])
  const ledger = ref([])
  const orders = ref([])
  const billingSummary = ref(null)
  const autoReload = ref(null)
  const autoReloadEligibleProductIds = ref([])
  let mergeTask = Promise.resolve(null)
  let loadTask = null

  const busy = computed(() => authBusy.value || billingBusy.value || checkoutBusy.value)
  // 登录状态看「有没有账号」，不看「access token 还新鲜吗」：token 过期由 transport 自动续，
  // 真登出会把 account 清成 null。掺进 access token 的话，token 一过期这个值就无声翻假，
  // 依赖它的余额、计费面板会静悄悄跳过不刷。
  const isSignedIn = computed(() => !!account.value?.id)
  const credits = computed(() => Number(account.value?.credits || 0))

  if (typeof api.onAuthExpired === 'function') {
    api.onAuthExpired(clearAccountState)
  }

  function setCreditsFromBalance(balance) {
    const points = Number(balance?.points)
    if (!Number.isFinite(points)) throw new Error('billing balance response missing points')
    account.value = {
      ...(account.value || {}),
      credits: points,
    }
    writeCachedAccount(account.value)
  }

  function normalizeMergeResult(payload) {
    const merged = payload?.merged || payload?.counts || payload || {}
    /** @type {Record<string, string[]>} */
    const aliases = {
      runs: ['runCount'],
      turns: ['turnCount'],
      progress: ['progressCount', 'userProgress'],
      saveSlots: ['slots', 'saveSlotCount'],
      endingRecords: ['endings', 'endingRecordCount'],
      events: ['eventCount'],
      agentCalls: ['agentCallCount'],
      billingLedger: ['ledger', 'ledgerCount'],
      billingUnlocks: ['unlocks', 'unlockCount'],
      billingFreeQuota: ['freeQuota', 'freeQuotaCount'],
      paymentOrders: ['orders', 'orderCount'],
    }
    const counts = emptyMergeCounts()
    for (const [key] of MERGE_COUNT_FIELDS) {
      counts[key] = countOf(merged[key] ?? firstAliasValue(merged, aliases[key] || []))
    }
    const total = mergeTotal(counts)
    return {
      status: total > 0 ? 'success' : 'empty',
      ...counts,
    }
  }

  async function mergeAnonymous() {
    const anonymousId = api.tokenStore?.getDeviceId?.()
    if (!anonymousId || typeof api.auth?.mergeAnonymous !== 'function') return null
    return api.auth.mergeAnonymous({ anonymousId })
  }

  function load() {
    if (loadTask !== null) return loadTask
    loadTask = loadAccount().finally(() => {
      loadTask = null
    })
    return loadTask
  }

  async function loadAccount() {
    if (!api.tokenStore.hasSession()) {
      account.value = null
      clearCachedAccount()
      return null
    }
    authBusy.value = true
    error.value = ''
    try {
      account.value = await api.account.getProfile()
      writeCachedAccount(account.value)
      return account.value
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err)
      return null
    } finally {
      authBusy.value = false
    }
  }

  async function authenticate(kind, payload) {
    authBusy.value = true
    error.value = ''
    mergeResult.value = null
    try {
      const response = await api.auth[AUTH_REQUESTS[kind]](payload)
      api.tokenStore?.setTokens?.(response)
      account.value = response.account
      writeCachedAccount(account.value)
      analytics.track(response.is_new_user ? 'sign_up' : 'login', { method: authMethod(kind, payload) })
      mergeResult.value = { status: 'pending', ...emptyMergeCounts() }
      mergeTask = mergeAnonymous()
        .then(merged => {
          mergeResult.value = normalizeMergeResult(merged)
          return mergeResult.value
        })
        .catch(() => {
          mergeResult.value = { status: 'failed', ...emptyMergeCounts() }
          return mergeResult.value
        })
      return response.account
    } catch (err) {
      error.value = friendlyAuthError(err, getLocale())
      throw err
    } finally {
      authBusy.value = false
    }
  }

  async function login({ email, password }) {
    return authenticate('login', { email, password })
  }

  async function loginWithEmailCode({ email, verification_code }) {
    return authenticate('emailCode', {
      email,
      verification_code,
      locale: getLocale(),
      device_id: api.tokenStore.getDeviceId(),
    })
  }

  async function sendLoginCode({ email }) {
    authBusy.value = true
    error.value = ''
    try {
      return await api.auth.sendCode({ channel: 'email', target: email, purpose: 'login', locale: getLocale() })
    } catch (err) {
      error.value = friendlyAuthError(err, getLocale())
      throw err
    } finally {
      authBusy.value = false
    }
  }

  async function loginWithOauth({ provider, code, redirect_uri, code_verifier, access_token }) {
    return authenticate('oauth', {
      provider,
      code,
      redirect_uri,
      code_verifier,
      access_token,
      locale: getLocale(),
      device_id: api.tokenStore.getDeviceId(),
    })
  }

  async function updateProfile(patch) {
    authBusy.value = true
    error.value = ''
    try {
      account.value = await api.account.updateProfile(patch)
      writeCachedAccount(account.value)
      return account.value
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err)
      throw err
    } finally {
      authBusy.value = false
    }
  }

  function waitForMerge() {
    return mergeTask
  }

  async function sendRegisterCode({ email }) {
    authBusy.value = true
    error.value = ''
    try {
      return await api.auth.sendCode({
        channel: 'email',
        target: email,
        purpose: 'register',
        locale: getLocale(),
      })
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err)
      throw err
    } finally {
      authBusy.value = false
    }
  }

  async function requestPasswordReset({ email }) {
    authBusy.value = true
    error.value = ''
    try {
      if (typeof api.auth?.requestPasswordReset !== 'function') throw new Error('password reset api unavailable')
      return await api.auth.requestPasswordReset({ email, locale: getLocale() })
    } catch (err) {
      error.value = friendlyAuthError(err, getLocale())
      throw err
    } finally {
      authBusy.value = false
    }
  }

  async function confirmPasswordReset({ email, password, verificationCode }) {
    authBusy.value = true
    error.value = ''
    try {
      if (typeof api.auth?.confirmPasswordReset !== 'function') throw new Error('password reset api unavailable')
      return await api.auth.confirmPasswordReset({ email, password, verificationCode })
    } catch (err) {
      error.value = friendlyAuthError(err, getLocale())
      throw err
    } finally {
      authBusy.value = false
    }
  }

  async function register({ email, password, verificationCode }) {
    return authenticate('register', {
      email,
      password,
      verification_code: verificationCode,
      locale: getLocale(),
      device_id: api.tokenStore.getDeviceId(),
    })
  }

  function clearAccountState() {
    account.value = null
    clearCachedAccount()
    checkoutUrl.value = ''
    checkoutStatus.value = ''
    mergeResult.value = null
    ledger.value = []
    orders.value = []
    error.value = ''
    billingError.value = ''
  }

  function clearSessionState() {
    api.tokenStore?.clear?.()
    clearAccountState()
  }

  function setSessionExpiredMessage(message = '') {
    error.value = message
  }

  async function logout() {
    let failure = null
    let failureMessage = ''
    const refreshToken = api.tokenStore?.getRefreshToken?.()
    if (refreshToken && typeof api.auth?.logout === 'function') {
      authBusy.value = true
      error.value = ''
      try {
        await api.auth.logout({ refresh_token: refreshToken })
      } catch (err) {
        error.value = err instanceof Error ? err.message : String(err)
        failureMessage = error.value
        failure = err
      } finally {
        authBusy.value = false
      }
    }
    if (failure) return { ok: false, error: failureMessage }
    clearSessionState()
    return { ok: true }
  }

  async function logoutAll() {
    let failure = null
    let failureMessage = ''
    if (typeof api.auth?.logoutAll === 'function') {
      authBusy.value = true
      error.value = ''
      try {
        await api.auth.logoutAll()
      } catch (err) {
        error.value = err instanceof Error ? err.message : String(err)
        failureMessage = error.value
        failure = err
      } finally {
        authBusy.value = false
      }
    }
    if (failure) return { ok: false, error: failureMessage }
    clearSessionState()
    return { ok: true }
  }

  async function refreshBalance() {
    if (!isSignedIn.value) return null
    assertPayApi('getBalance')
    const balance = await api.pay.getBalance()
    setCreditsFromBalance(balance)
    return balance
  }

  async function loadBilling() {
    billingBusy.value = true
    billingError.value = ''
    try {
      assertPayApi('listProducts')
      products.value = productsFromResponse(await api.pay.listProducts())
      if (!isSignedIn.value) {
        ledger.value = []
        orders.value = []
        return null
      }
      assertPayApi('getBillingSummary')
      assertPayApi('listLedger')
      assertPayApi('listOrders')
      const [summary, nextLedger, nextOrders] = await Promise.all([
        api.pay.getBillingSummary(),
        api.pay.listLedger({ limit: 12 }),
        api.pay.listOrders({ limit: 12 }),
      ])
      setCreditsFromBalance(summary)
      billingSummary.value = summary
      ledger.value = pageItemsFromResponse(nextLedger, 'billing ledger')
      orders.value = pageItemsFromResponse(nextOrders, 'payment orders')
      return { summary, ledger: ledger.value, orders: orders.value, products: products.value }
    } catch (err) {
      billingError.value = err instanceof Error ? err.message : String(err)
      throw err
    } finally {
      billingBusy.value = false
    }
  }

  async function buyPack(packId) {
    checkoutBusy.value = true
    error.value = ''
    billingError.value = ''
    checkoutUrl.value = ''
    checkoutStatus.value = 'pending'
    try {
      if (!packId) throw new Error('billing product id is required')
      assertPayApi('creditsCheckout')
      const result = await api.pay.creditsCheckout({ pack_id: packId, ...checkoutReturnUrls() })
      checkoutUrl.value = result.url || ''
      if (result.url && typeof window !== 'undefined') window.location.href = result.url
      return result
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err)
      checkoutStatus.value = ''
      throw err
    } finally {
      checkoutBusy.value = false
    }
  }

  async function loadAutoReload() {
    assertPayApi('getAutoReload')
    const result = await api.pay.getAutoReload()
    if (!result || !Array.isArray(result.eligible_product_ids)) throw new Error('auto-reload response is invalid')
    autoReload.value = result.setting
    autoReloadEligibleProductIds.value = result.eligible_product_ids
    return result
  }

  async function setupAutoReload({ product_id, threshold_points, consented }) {
    assertPayApi('setupAutoReload')
    const urls = checkoutReturnUrls()
    const success = new URL(urls.success_url)
    const cancel = new URL(urls.cancel_url)
    success.searchParams.set('creditTab', 'auto')
    success.searchParams.set('autoReload', 'success')
    cancel.searchParams.set('creditTab', 'auto')
    cancel.searchParams.set('autoReload', 'cancel')
    const result = await api.pay.setupAutoReload({
      product_id,
      threshold_points,
      consented,
      success_url: success.toString(),
      cancel_url: cancel.toString(),
    })
    if (typeof result.url !== 'string' || result.url === '') throw new Error('auto-reload setup URL is missing')
    window.location.href = result.url
  }

  async function updateAutoReload(input) {
    assertPayApi('updateAutoReload')
    autoReload.value = await api.pay.updateAutoReload(input)
  }

  async function disableAutoReload() {
    assertPayApi('disableAutoReload')
    autoReload.value = await api.pay.disableAutoReload()
  }

  async function claimDailyPoints() {
    billingBusy.value = true
    billingError.value = ''
    try {
      assertPayApi('dailyClaim')
      const result = await api.pay.dailyClaim()
      setCreditsFromBalance(result)
      await loadBilling()
      return result
    } finally {
      billingBusy.value = false
    }
  }

  /**
   * 从 Stripe 结账页跳回来：checkoutReturnUrls() 往回跳地址里塞的 `checkout` 参数在这里消费。
   * 读完立刻从地址栏删掉，刷新页面不会再重放一次。账务刷新完看最新一笔订单：
   * 已 paid 就报「已到账 N 积分」，还 pending 说明 webhook 没到，报「到账中」。提示挂几秒自动收起。
   * 返回本次的 checkoutStatus，地址里没带参数返回 null，调用方据此决定是否走普通加载。
   */
  async function handleCheckoutReturn() {
    const returned = takeCheckoutReturnStatus()
    if (returned === null) return null
    checkoutUrl.value = ''
    await loadBilling()
    const latest = orders.value[0]
    if (returned === 'cancel') {
      checkoutStatus.value = 'cancel'
      checkoutPoints.value = 0
    } else {
      checkoutStatus.value = latest?.status === 'paid' ? 'credited' : 'confirming'
      checkoutPoints.value = latest?.total_points ?? 0
    }
    clearTimeout(checkoutNoticeTimer)
    checkoutNoticeTimer = setTimeout(clearCheckoutStatus, CHECKOUT_NOTICE_MS)
    return checkoutStatus.value
  }

  function takeCheckoutReturnStatus() {
    if (typeof window === 'undefined') return null
    const url = new URL(window.location.href)
    const status = url.searchParams.get('checkout')
    if (status === null) return null
    url.searchParams.delete('checkout')
    window.history.replaceState(window.history.state, '', url.toString())
    return status === 'success' || status === 'cancel' ? status : null
  }

  function clearCheckoutStatus() {
    clearTimeout(checkoutNoticeTimer)
    checkoutStatus.value = ''
  }

  function checkoutReturnUrls() {
    if (typeof window === 'undefined') return {}
    const success = new URL(window.location.href)
    const cancel = new URL(window.location.href)
    success.searchParams.set('checkout', 'success')
    cancel.searchParams.set('checkout', 'cancel')
    success.hash = ''
    cancel.hash = ''
    return { success_url: success.toString(), cancel_url: cancel.toString() }
  }

  /**
   * @param {Record<string, any>} source
   * @param {string[]} keys
   */
  function firstAliasValue(source, keys) {
    for (const key of keys) {
      if (source[key] != null) return source[key]
    }
    return undefined
  }

  function assertPayApi(method) {
    if (typeof api.pay?.[method] !== 'function') throw new Error(`payment api ${method} unavailable`)
  }

  function productsFromResponse(payload) {
    if (!Array.isArray(payload)) throw new Error('billing products response must be an array')
    return payload
  }

  function pageItemsFromResponse(payload, label) {
    if (!payload || !Array.isArray(payload.items)) throw new Error(`${label} response missing items`)
    return payload.items
  }

  return {
    account,
    autoReload,
    autoReloadEligibleProductIds,
    authBusy,
    billingError,
    billingSummary,
    billingBusy,
    busy,
    checkoutBusy,
    checkoutNotice,
    checkoutStatus,
    checkoutUrl,
    credits,
    error,
    isSignedIn,
    ledger,
    mergeResult,
    orders,
    products,
    buyPack,
    claimDailyPoints,
    clearCheckoutStatus,
    setSessionExpiredMessage,
    handleCheckoutReturn,
    load,
    loadBilling,
    loadAutoReload,
    login,
    loginWithOauth,
    loginWithEmailCode,
    sendLoginCode,
    logout,
    logoutAll,
    refreshBalance,
    register,
    waitForMerge,
    requestPasswordReset,
    confirmPasswordReset,
    sendRegisterCode,
    updateProfile,
    setupAutoReload,
    updateAutoReload,
    disableAutoReload,
  }
}

function authMethod(kind, payload) {
  if (kind === 'oauth') return payload.provider
  if (kind === 'login' || kind === 'register' || kind === 'emailCode') return 'email'
  throw new Error(`Unhandled authentication kind: ${kind}`)
}

function readCachedAccount() {
  try {
    const value = localStorage.getItem(ACCOUNT_CACHE_KEY)
    if (value === null) return null
    return parseAccount(JSON.parse(value))
  } catch {
    clearCachedAccount()
    return null
  }
}

function writeCachedAccount(account) {
  const value = JSON.stringify(parseAccount(account))
  try {
    localStorage.setItem(ACCOUNT_CACHE_KEY, value)
  } catch {
    // Account cache is an optional first-paint optimization.
  }
}

function clearCachedAccount() {
  try {
    localStorage.removeItem(ACCOUNT_CACHE_KEY)
  } catch {
    // Storage can be unavailable in private browsing contexts.
  }
}
